import EventBus from '@/modules/core/app/helpers/EventBus';
import { commits, dispatches } from '@/modules/core/app/helpers/store';
import { DashboardRoute } from '@/modules/ta/dashboard/dashboard.constants';
import { ReportStudioRoute } from '@/modules/ta/reportstudio/reportstudio.constants';
import {
  MCPCHAT_STORAGE_KEY,
  McpChatEvent,
  McpChatState,
  MCPCHAT_HISTORY_PAGE_SIZE,
  McpChatNavigationTargetType,
  McpChatQuickReplyOption,
} from '../constants/mcpchat.constants';
import McpChatService from '../services/McpChatService';

// ApiError.message is response.data which the backend sends as an array of strings.
// Mirror the pattern from dashboard.actions.js to extract a readable string.
function extractErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  let message = error?.message;
  if (Array.isArray(message)) {
    [message] = message;
  }
  if (!message || typeof message !== 'string') {
    message = fallback;
  }
  return message;
}

function appendProgressStep(steps, seen, message) {
  if (!message || seen.has(message)) {
    return;
  }

  seen.add(message);
  steps.push(message);
}

function buildLoadingProgressMessages(messageText = '', pageContext = null) {
  const normalizedText = String(messageText || '').toLowerCase();
  const steps = [];
  const seen = new Set();

  appendProgressStep(steps, seen, 'Analyzing your request');

  [
    ['google ads', 'Querying Google Ads data'],
    ['facebook ads', 'Querying Facebook Ads data'],
    ['linkedin ads', 'Querying LinkedIn Ads data'],
    ['google analytics 4', 'Querying Google Analytics 4 data'],
    ['ga4', 'Querying Google Analytics 4 data'],
    ['google analytics', 'Querying Google Analytics data'],
    ['microsoft ads', 'Querying Microsoft Ads data'],
    ['bing ads', 'Querying Bing Ads data'],
    ['amazon ads', 'Querying Amazon Ads data'],
    ['tiktok ads', 'Querying TikTok Ads data'],
    ['instagram', 'Querying Instagram data'],
    ['youtube', 'Querying YouTube data'],
    ['shopify', 'Querying Shopify data'],
    ['salesforce', 'Querying Salesforce data'],
  ].forEach(([keyword, label]) => {
    if (normalizedText.includes(keyword)) {
      appendProgressStep(steps, seen, label);
    }
  });

  if (!steps.some((step) => step.startsWith('Querying '))) {
    appendProgressStep(steps, seen, 'Looking up the relevant data source');
  }

  if (
    /\b(create|add|build|generate)\b.*\bwidget\b|\bwidget\b.*\b(create|add|build|generate)\b/.test(
      normalizedText
    )
  ) {
    appendProgressStep(steps, seen, 'Preparing the widget configuration');
  }

  if (normalizedText.includes('dashboard')) {
    appendProgressStep(steps, seen, 'Checking dashboard placement details');
  }

  if (normalizedText.includes('report')) {
    appendProgressStep(steps, seen, 'Checking report placement details');
  }

  if (pageContext?.page_id) {
    appendProgressStep(steps, seen, 'Checking the current dashboard context');
  }

  if (pageContext?.report_id) {
    appendProgressStep(steps, seen, 'Checking the current report context');
  }

  appendProgressStep(steps, seen, 'Preparing the response');

  return steps;
}

function loadPersistedState() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(MCPCHAT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function getPageContext() {
  // Grok is hash-routed (#/dashboards/3450/...), so we read from
  // window.location.hash rather than pathname. Intentionally avoids
  // importing @/router to keep the mcpchat subtree a leaf in the static
  // graph (see commit 0bdd43bc5721).
  const hash = typeof window !== 'undefined' && window.location ? window.location.hash : '';
  const path = hash.startsWith('#') ? hash.slice(1).split('?')[0] : '';
  const ctx = {};

  // Dashboard: /dash/{id}[/section/{layout_id}], /dashboards/{id}[/section/{layout_id}], fav/ variants
  const dashMatch = path.match(/\/(?:fav\/)?(?:dash|dashboards)\/(\d+)(?:\/section\/(\d+))?/);
  if (dashMatch) {
    ctx.page_id = Number(dashMatch[1]);
    if (dashMatch[2]) {
      ctx.layout_id = Number(dashMatch[2]);
    }
  }

  // Report Studio: /reportstudio/{id}, /nui/reportstudio/{id}, /fav/reportstudio/{id}, /fav/nui/reportstudio/{id}
  const reportMatch = path.match(/\/(?:fav\/)?(?:nui\/)?reportstudio\/(\d+)/);
  if (reportMatch) {
    ctx.report_id = Number(reportMatch[1]);
  }

  return Object.keys(ctx).length > 0 ? ctx : null;
}

function normalizeActionResponse(selectedValues) {
  if (!Array.isArray(selectedValues) || selectedValues.length !== 1) {
    return selectedValues?.join(', ') || '';
  }

  const [value] = selectedValues;
  const normalizedKey = String(value || '')
    .trim()
    .toLowerCase();
  const normalized = {
    [McpChatQuickReplyOption.EXISTING_DASHBOARD.toLowerCase()]:
      'Create it on an existing dashboard. First show up to 5 recent matching dashboards as chat options if the target is not already obvious. If I reply with a dashboard name, resolve it yourself.',
    [McpChatQuickReplyOption.NEW_DASHBOARD.toLowerCase()]: 'Create it on a new dashboard.',
    [McpChatQuickReplyOption.NEW_REPORT_STUDIO.toLowerCase()]:
      'Create it in Report Studio as a new report.',
  };

  return normalized[normalizedKey] || value;
}

function getNavigationPath(target) {
  if (!target || typeof target !== 'object') {
    return null;
  }

  if (target.type === McpChatNavigationTargetType.DASHBOARD && Number(target.page_id) > 0) {
    return `/${DashboardRoute.DASHBOARDS}/${Number(target.page_id)}`;
  }

  if (target.type === McpChatNavigationTargetType.REPORT && Number(target.report_id) > 0) {
    return `/${ReportStudioRoute.NUI_REPORTSTUDIO}/${Number(target.report_id)}`;
  }

  return null;
}

// Resolves the response's navigation_target and dispatches the appropriate
// side effect. Keeps @/router out of this module so the mcpchat subtree stays
// a leaf in the static graph (no Grok SPA drag-in). For actual route changes,
// we emit a McpChatEvent.NAVIGATE signal; TapClicksAiChat.vue listens and
// performs router.push / window.open using its own this.$router access.
function dispatchNavigation(target) {
  const path = getNavigationPath(target);
  if (!path) {
    return;
  }

  const currentPageContext = getPageContext();
  const isOnDashboardOrReportPage =
    Number(currentPageContext?.page_id) > 0 || Number(currentPageContext?.report_id) > 0;
  const isCurrentDashboardTarget =
    target.type === McpChatNavigationTargetType.DASHBOARD &&
    Number(target.page_id) > 0 &&
    Number(currentPageContext?.page_id) === Number(target.page_id);
  const isCurrentReportTarget =
    target.type === McpChatNavigationTargetType.REPORT &&
    Number(target.report_id) > 0 &&
    Number(currentPageContext?.report_id) === Number(target.report_id);

  if (isCurrentDashboardTarget || isCurrentReportTarget) {
    if (isCurrentDashboardTarget) {
      dispatches.dashboard.setNeedsReload(true);
    } else {
      dispatches.reportstudio.setNeedsReload(true);
    }
    return;
  }

  const openInNewTab = Boolean(target.is_new_target) || !isOnDashboardOrReportPage;
  EventBus.signal(McpChatEvent.NAVIGATE, { path, openInNewTab });
}

export const actions = {
  hydrate({ state }) {
    if (state.isHydrated) {
      return {
        chatState: state.chatState,
        conversationId: state.conversationId,
        error: state.error,
      };
    }

    const persistedState = loadPersistedState();
    commits.mcpchat.hydrateState(persistedState);

    return persistedState;
  },

  async loadSuggestionPrompts({ state }) {
    if (state.isSuggestionPromptsLoaded) {
      return;
    }

    try {
      const response = await McpChatService.getSuggestionPrompts();
      commits.mcpchat.setSuggestionPrompts(response.prompts || []);
    } catch (error) {
      commits.mcpchat.setSuggestionPrompts([]);
    }
  },

  async sendMessage({ state }, payload) {
    const messageText = typeof payload === 'string' ? payload : payload?.messageText;
    const displayText = typeof payload === 'string' ? payload : payload?.displayText || messageText;
    const explicitPageContext = typeof payload === 'string' ? null : payload?.pageContext || null;
    const { requestVersion } = state;
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: displayText,
      timestamp: new Date().toISOString(),
    };
    commits.mcpchat.addMessage(userMessage);
    commits.mcpchat.setLoading(true);
    commits.mcpchat.setLoadingProgressMessages(
      buildLoadingProgressMessages(messageText, explicitPageContext || getPageContext())
    );
    commits.mcpchat.setError(null);

    try {
      const pageContext = explicitPageContext || getPageContext();
      const response = await McpChatService.sendMessage(
        messageText,
        state.conversationId,
        pageContext
      );
      if (state.requestVersion !== requestVersion) {
        return;
      }

      if (response.conversation_id) {
        commits.mcpchat.setConversationId(response.conversation_id);
      }
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        toolsUsed: response.tools_used || [],
        actions: response.actions || [],
        blocks: response.blocks || [],
        debug: response.debug || null,
        navigationTarget: response.navigation_target || null,
        llmCallMetadata: response.llm_call_metadata || null,
        llmTotalCost: response.llm_total_cost ?? null,
      };
      commits.mcpchat.addMessage(assistantMessage);

      if (response.navigation_target) {
        dispatchNavigation(response.navigation_target);
      }

      if (state.isHistoryOpen) {
        dispatches.mcpchat.loadHistory({ reset: true });
      }
    } catch (error) {
      if (state.requestVersion !== requestVersion) {
        return;
      }

      const errorMessage = extractErrorMessage(error);
      commits.mcpchat.setError(errorMessage);
      commits.mcpchat.addMessage({
        id: Date.now() + 1,
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date().toISOString(),
        isError: true,
      });
    } finally {
      if (state.requestVersion === requestVersion) {
        commits.mcpchat.setLoading(false);
      }
    }
  },

  toggleChat({ state }) {
    if (state.chatState === McpChatState.COLLAPSED) {
      commits.mcpchat.setChatState(McpChatState.SMALL);
    } else {
      commits.mcpchat.setChatState(McpChatState.COLLAPSED);
    }
  },

  expandChat({}) {
    commits.mcpchat.setChatState(McpChatState.EXPANDED);
  },

  collapseChat({}) {
    commits.mcpchat.setChatState(McpChatState.SMALL);
  },

  closeChat({}) {
    commits.mcpchat.setChatState(McpChatState.COLLAPSED);
    commits.mcpchat.setHistoryOpen(false);
  },

  async loadHistory({ state }, { reset = false } = {}) {
    const shouldReset = Boolean(reset);
    const isInitialLoad = shouldReset || state.historyItems.length === 0;

    if (state.isHistoryLoading || state.isHistoryLoadingMore) {
      return;
    }

    if (!shouldReset && !state.hasMoreHistory && state.historyItems.length > 0) {
      return;
    }

    if (shouldReset) {
      commits.mcpchat.invalidateHistoryRequests();
      commits.mcpchat.resetHistoryPagination();
    }

    const { historyRequestVersion } = state;

    if (isInitialLoad) {
      commits.mcpchat.setHistoryLoading(true);
    } else {
      commits.mcpchat.setHistoryLoadingMore(true);
    }

    try {
      const response = await McpChatService.getHistory({
        limit: MCPCHAT_HISTORY_PAGE_SIZE,
        offset: shouldReset ? 0 : state.historyNextOffset,
      });
      if (state.historyRequestVersion !== historyRequestVersion) {
        return;
      }
      commits.mcpchat.setHistoryPage({
        items: response.items || [],
        reset: shouldReset,
        hasMoreData: response.has_more_data,
        nextOffset: response.next_offset,
      });
    } finally {
      if (state.historyRequestVersion === historyRequestVersion) {
        if (isInitialLoad) {
          commits.mcpchat.setHistoryLoading(false);
        } else {
          commits.mcpchat.setHistoryLoadingMore(false);
        }
      }
    }
  },

  async openHistory({}) {
    commits.mcpchat.setHistoryOpen(true);
    await dispatches.mcpchat.loadHistory({ reset: true });
  },

  async loadMoreHistory({}) {
    await dispatches.mcpchat.loadHistory();
  },

  closeHistory({}) {
    commits.mcpchat.setHistoryOpen(false);
  },

  async resumeConversation({}, conversationId) {
    if (!conversationId) {
      return;
    }

    commits.mcpchat.invalidatePendingRequests();
    commits.mcpchat.setHistoryLoading(true);

    try {
      const response = await McpChatService.getConversation(conversationId);
      commits.mcpchat.restoreConversation({
        conversationId: response.conversation_id,
        messages: response.messages || [],
      });
    } finally {
      commits.mcpchat.setHistoryLoading(false);
    }
  },

  submitActionResponse({}, { messageId, actionId, selectedValues }) {
    commits.mcpchat.markActionSubmitted({ messageId, actionId });
    const text = normalizeActionResponse(selectedValues);
    dispatches.mcpchat.sendMessage(text);
  },

  async executeAgent({ state }, { messageId, actionId, agentKey, contextPayload }) {
    if (actionId) {
      commits.mcpchat.markActionSubmitted({ messageId, actionId });
    }

    const { requestVersion } = state;

    commits.mcpchat.setLoading(true);
    // eslint-disable-next-line tap/no-raw-text-js
    commits.mcpchat.setLoadingProgressMessages([
      `Running ${agentKey.replace(/_/g, ' ')}...`,
      'Analyzing data',
      'Preparing results',
    ]);
    commits.mcpchat.setError(null);

    try {
      const pageContext = getPageContext();
      const response = await McpChatService.executeAgent(
        agentKey,
        contextPayload,
        state.conversationId,
        pageContext
      );

      if (state.requestVersion !== requestVersion) {
        return;
      }

      if (response.conversation_id) {
        commits.mcpchat.setConversationId(response.conversation_id);
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        toolsUsed: response.tools_used || [],
        actions: response.actions || [],
        blocks: response.blocks || [],
        debug: response.debug || null,
        _agentKey: agentKey,
        _contextPayload: contextPayload,
      };
      commits.mcpchat.addMessage(assistantMessage);

      if (state.isHistoryOpen) {
        dispatches.mcpchat.loadHistory({ reset: true });
      }
    } catch (error) {
      if (state.requestVersion !== requestVersion) {
        return;
      }

      const errorMessage = extractErrorMessage(error, 'Agent execution failed. Please try again.');
      commits.mcpchat.setError(errorMessage);
      commits.mcpchat.addMessage({
        id: Date.now() + 1,
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date().toISOString(),
        isError: true,
      });
    } finally {
      if (state.requestVersion === requestVersion) {
        commits.mcpchat.setLoading(false);
      }
    }
  },

  clearChat({}) {
    commits.mcpchat.invalidatePendingRequests();
    commits.mcpchat.clearMessages();
  },
};
